// Validates email format (xxx@xxx.com)
const isValidEmailFormat = (email) => {
  if (!email || typeof email !== "string") return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

// Formats email (trim and lowercase)
const formatEmail = (email) => {
  if (!email || typeof email !== "string") return email;
  return email.trim().toLowerCase();
};

// Sanitizes enhanced_conversion_data by removing invalid emails and empty values
const sanitizeEnhancedConversionData = (data) => {
  if (!data || typeof data !== "object") return data;

  const sanitized = { ...data };

  // Format and validate email
  if (sanitized.email !== undefined) {
    const formattedEmail = formatEmail(sanitized.email);
    if (isValidEmailFormat(formattedEmail)) {
      sanitized.email = formattedEmail;
    } else {
      // Remove invalid email
      delete sanitized.email;
    }
  }

  // Remove empty strings, null, and undefined values
  Object.keys(sanitized).forEach((key) => {
    const value = sanitized[key];
    if (value === "" || value === null || value === undefined) {
      delete sanitized[key];
    }
  });

  return sanitized;
};

const sendTrackingEvent = (params) => {
  if (typeof dataLayer === "undefined") return;
  try {
    // Sanitize enhanced_conversion_data if present
    let sanitizedParams = { ...params };
    if (sanitizedParams.enhanced_conversion_data) {
      sanitizedParams.enhanced_conversion_data = sanitizeEnhancedConversionData(
        sanitizedParams.enhanced_conversion_data
      );
    }

    const dataLayerParams =
      process.env.NEXT_PUBLIC_JSW_ENV != "prod"
        ? { environment: "staging", ...sanitizedParams }
        : { ...sanitizedParams };

    dataLayer?.push(dataLayerParams);
  } catch (error) {
    // tracking error
  }
};

const signupTracking = (
  url,
  origin,
  job,
  company,
  isSocialLogin = false,
  socialProvider = null
) => {
  let signupMedium = isSocialLogin ? "social" : "internal";
  let signupSource = "";

  const safeUrl = url || "";
  const safeOrigin = origin || "";
  const safeJob = job || {};
  const safeCompany = company || {};

  switch (safeUrl) {
    case "/about-us":
      signupSource = "about_us_navbar";
      break;
    case "/community-guidelines":
      signupSource = "community_guidelines_navbar";
      break;
    case "/contact-us":
      signupSource = "contact_us_navbar";
      break;
    case "/faq":
      signupSource = "faq_navbar";
      break;
    case "/privacy-policy":
      signupSource = "privacy_policy_navbar";
      break;
    case "/terms-and-conditions":
      signupSource = "terms_and_conditions_navbar";
      break;
    case "/update-resume":
      signupSource = "update_resume_navbar";
      break;
    case "/updates":
      signupSource = "updates_navbar";
      break;
    default:
      signupSource = "hp_navbar";
      break;
  }

  if (safeUrl.includes("/apply")) {
    if (safeOrigin === "search") {
      signupSource = safeJob.category
        ? `jlp_easy_apply_${safeJob.category}`
        : signupSource;
    } else if (safeOrigin === "individual_job_page") {
      signupSource = safeJob.category
        ? `jdp_easy_apply_${safeJob.category}`
        : signupSource;
    } else if (safeOrigin === "jdp_view_similar_jobs") {
      signupSource = "jdp_view_similar_jobs";
    } else if (safeOrigin === "jlp_view_similar_jobs") {
      signupSource = "jlp_view_similar_jobs";
    } else {
      signupSource = "";
    }
  } else if (safeUrl.includes("/companies")) {
    signupSource = "clp_navbar";

    if (safeCompany && Object.keys(safeCompany).length > 0) {
      signupSource = safeCompany.category
        ? `cdp_navbar_${safeCompany.category}`
        : signupSource;
    }
  } else if (safeUrl.includes("/jobs")) {
    if (safeUrl === "/jobs" || safeUrl.includes("/jobs?")) {
      signupSource = "jlp_navbar";

      if (safeOrigin === "jobAlert") {
        signupSource = "jlp_job_alert";
      }
    } else if (safeOrigin === "jobAlert") {
      signupSource = "jdp_job_alert";
    } else if (safeJob && Object.keys(safeJob).length > 0) {
      signupSource = safeJob.category
        ? `jdp_navbar_${safeJob.category}`
        : signupSource;
    }
  }

  if (safeOrigin === "homepage-drop-resume") {
    signupSource = "hp_drop_resume";
  }

  if (safeUrl.includes("/sitemap")) {
    signupSource = "sitemap_navbar";
  }

  if (safeUrl === "/drop-resume") {
    signupSource = "drlp_navbar";
    if (safeOrigin === "drop_resume") {
      signupSource = "drlp_drop_resume";
    }
  }

  if (safeUrl === "/drop-resume-hiredly-x") {
    signupSource = "hx_drp_navbar";
    if (safeOrigin === "hx_drop_resume") {
      signupSource = "hx_drp_drop_resume";
    }
  }

  if (safeUrl === "/internships" || safeUrl.includes("/internships")) {
    signupSource = "ibp_navbar";
    if (safeOrigin === "internship_drop_resume") {
      signupSource = "ibp_drop_resume";
    }
  }

  if (
    safeUrl === "/management-trainee" ||
    safeUrl.includes("/management-trainee")
  ) {
    signupSource = "mtp_navbar";
    if (safeOrigin === "management_trainee_drop_resume") {
      signupSource = "mtp_drop_resume";
    }
  }

  if (safeUrl === "/worthy" || safeUrl.includes("/worthy")) {
    signupSource = "jwc_navbar";
    if (safeOrigin === "jwc_redirect") {
      signupSource = "jwc_redirect";
    }
  }

  if (safeOrigin.includes("_floating_resume")) {
    signupSource = safeOrigin;
  }

  if (safeOrigin === "job_recommendation") {
    signupSource = "jlp_fyp";
  }

  if (safeUrl === "/advice" || safeUrl.includes("/advice?page=")) {
    signupSource = "cap_landing_navbar";
  } else if (safeUrl.includes("/advice")) {
    signupSource = "cap_article_navbar";
  }

  if (isSocialLogin && socialProvider) {
    signupSource = signupSource
      ? `${signupSource}_${socialProvider}`
      : `social_login_${socialProvider}`;
  } else if (!isSocialLogin) {
    signupSource = signupSource ? `${signupSource}_email` : `email_login`;
  }

  if (process.env.NODE_ENV !== "production") {
    console.log("Signup Tracking Debug:", {
      input: { url, origin, job, company, isSocialLogin, socialProvider },
      output: { signupMedium, signupSource },
    });
  }

  if (safeOrigin === "company-profile") {
    signupSource = "company-profile";
  }

  return { signupMedium, signupSource };
};

export { sendTrackingEvent, signupTracking };
